import getGlobal from '@/utils/shared/getGlobal';

const tipsUbbREG = /\[tips(?:=(.*?))?](.*?)\[\/tips]/g;

const MIN_SPACE = 10;

enum EDefaultConfig {
    THEME = 'light',
    ICON_COLOR = 'inherit',
    ICON_PC_SIZE = '20',
    ICON_MOBILE_SIZE = '18'
}

interface IWinWithTipsTag extends Window{
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __isAddTipsTextEvent: boolean;
}

const win = getGlobal() as unknown as IWinWithTipsTag;

const handleMouseOver = (event: MouseEvent): void => {

    const target = event.target as HTMLElement;
    const content = target.querySelector('.__reo_ubb_tips-content');

    if (content && content instanceof HTMLElement) {
        content.style.display = 'block';

        const screenWidth = win.innerWidth;
        const targetRect = target.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();

        // 默认tips气泡始终在目标元素的正上方 10px 处水平居中
        let top = targetRect.top - contentRect.height - MIN_SPACE;
        let left = targetRect.left + targetRect.width / 2 - contentRect.width / 2;

        // 超出屏幕，调整到屏幕最右方
        if (left + contentRect.width > screenWidth) {
            left = screenWidth - contentRect.width - MIN_SPACE;
        }
        // 超出屏幕，调整到屏幕最左方
        if (left < 0) {
            left = MIN_SPACE;
        }
        // 超出屏幕，调整到屏幕最下方，并确定tips气泡尖的朝向
        if (top < 0) {
            top = targetRect.top + targetRect.height + MIN_SPACE;
            target.classList.remove('__reo_ubb_tips-trigger-down');
            target.classList.add('__reo_ubb_tips-trigger-top');
        }
        else {
            target.classList.remove('__reo_ubb_tips-trigger-top');
            target.classList.add('__reo_ubb_tips-trigger-down');
        }

        // 设置 tips 的位置
        content.style.top = `${top}px`;
        content.style.left = `${left}px`;

    }
};

const handleMouseLeave = (target: HTMLElement, content: Element | null) => (event: MouseEvent): void => {

    const relatedTarget = event.relatedTarget as HTMLElement;

    // 如果鼠标进入到 __reo_ubb_tips-content，则不隐藏
    if (relatedTarget?.classList.contains('__reo_ubb_tips-content')) {
        return;
    }

    // 否则隐藏
    if (content && content instanceof HTMLElement) {
        content.style.display = 'none';  // 隐藏 __reo_ubb_tips-content 元素
        target.classList.remove('__reo_ubb_tips-trigger-down');
        target.classList.remove('__reo_ubb_tips-trigger-top');
    }
};

const handleMouseOut = (event: MouseEvent): void => {

    const target = event.target as HTMLElement;
    const content = target.querySelector('.__reo_ubb_tips-content');

    // 进入到__reo_ubb_tips-content也不会让tips气泡隐藏
    target.addEventListener('mouseleave', handleMouseLeave(target, content));
};

/**
 * 根据参数返回tips
 * 支持设置light或dark主题、icon颜色、icon尺寸，icon PC端尺寸,icon M端尺寸
 * [tips=主题,icon颜色,icon PC端尺寸,icon M端尺寸]tips文案[/tips]
 * `eg. [tips=dark,#fff,18，12]tips文案[/tips]`
 * @param text
 * @returns
 */
function handleTipsText(text: string): string {

    if (!tipsUbbREG.test(text)) {
        return text;
    }

    return text.replace(tipsUbbREG, (matchedTag, options, value) => {
        try {

            const [
                theme = EDefaultConfig.THEME,
                iconColor = EDefaultConfig.ICON_COLOR,
                iconPCSize = EDefaultConfig.ICON_PC_SIZE,
                iconMobileSize = EDefaultConfig.ICON_MOBILE_SIZE
            ] = options ? options.split(',') : [];

            if (win?.document && !win.__isAddTipsTextEvent) {

                const triggerArr = Array.from(win.document.querySelectorAll('.__reo_ubb_tips-trigger'));

                for (const trigger of triggerArr) {
                    trigger.addEventListener('mouseover', handleMouseOver);
                    trigger.addEventListener('mouseout', handleMouseOut);
                }

                win.__isAddTipsTextEvent = true;
            }

            return `<span><style>
                        .__reo_ubb_tips-container {
                            position: relative;
                            display: inline-block;
                        }
                        .__reo_ubb_tips-trigger {
                            position: relative;
                            cursor: pointer;
                            vertical-align: text-top;
                        }
                        .__reo_ubb_tips-font-size-${iconPCSize}-${iconMobileSize}{
                            font-size: ${iconPCSize}px;
                             @media (max-width: 999px){
                                font-size: ${iconMobileSize}px;
                            }
                        }
                        .__reo_ubb_tips-trigger:hover{
                            color: var(--b_6);
                        }
                        .__reo_ubb_tips-content {
                            position: fixed;
                            background-color: var(--white);
                            color: var(--font_primary);
                            font-size: 14px;
                            font-weight: 500;
                            line-height: 1.2;
                            text-align: left;
                            padding: 16px;
                            border-radius: 5px;
                            box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.26);
                            max-width: 280px;
                            word-wrap: break-word;
                            width: max-content;
                            display: none;
                            z-index: 10;
                        }
                        .__reo_ubb_tips-trigger.__reo_ubb_tips-trigger-top::after {
                            content: '';
                            position: absolute;
                            bottom: -10px;
                            left: 50%;
                            transform: translateX(-50%);
                            border-width: 6px;
                            border-style: solid;
                            border-color: transparent transparent var(--white) transparent;
                            z-index: 10;
                        }
                        .__reo_ubb_tips-trigger.__reo_ubb_tips-trigger-down::after {
                            content: '';
                            position: absolute;
                            top: -10px;
                            left: 50%;
                            transform: translateX(-50%);
                            border-width: 6px;
                            border-style: solid;
                            border-color: var(--white) transparent transparent transparent;
                            z-index: 10;
                        }
                        .__reo_ubb_tips-trigger.__reo_ubb_tips-trigger-top[data-theme="dark"]::after {
                            content: '';
                            position: absolute;
                            bottom: -10px;
                            left: 50%;
                            transform: translateX(-50%);
                            border-width: 6px;
                            border-style: solid;
                            border-color: transparent transparent var(--black8) transparent;
                            z-index: 10;
                        }
                        .__reo_ubb_tips-trigger.__reo_ubb_tips-trigger-down[data-theme="dark"]::after {
                            content: '';
                            position: absolute;
                            top: -10px;
                            left: 50%;
                            transform: translateX(-50%);
                            border-width: 6px;
                            border-style: solid;
                            border-color: var(--black8) transparent transparent transparent;
                            z-index: 10;
                        }
                        .__reo_ubb_tips-content[data-theme="dark"] {
                            background-color: var(--black8);
                            color: var(--font_primary_dark);
                        }
                        .__reo_ubb_tips-content[data-theme="dark"]::after {
                            border-color: var(--black8) transparent transparent transparent;
                        }
                    </style><span class="__reo_ubb_tips-container">
                        <span
                            style="color: ${iconColor};"
                            class="__reo_ubb_tips-trigger icon-co_tips_line __reo_ubb_tips-font-size-${iconPCSize}-${iconMobileSize}"
                            data-theme="${theme}" 
                        >
                        <span
                            class="__reo_ubb_tips-content" 
                            data-theme="${theme}" 
                        >
                            ${value}
                        </span>
                    </span>
                </span>`;
        }
        catch (e) {
            const errorMessages: string[] = ['Replace tips text error'];
            errorMessages.push(`matchedTag: ${matchedTag}`);
            errorMessages.push(`options: ${options}`);
            console.error(errorMessages.join(','), e);
        }
        return '';
    });
}

export default handleTipsText;
